<template>
  <div v-if="chart_data">
    <ChartOptions
      :settings_menu_props="settings_menu_props"
      :config="general_config"
      :types="types"
      :is_loading="is_loading"
      @updateVisibleLines="updateVisibleLines($event)"
      @updateDataPoints="updateDataPoints($event)"
      @updateColorPalette="updateColorPalette($event)"
      :under_development="under_development"
    />

    <v-fade-transition>
      <v-btn
        v-if="is_chart_modified"
        class="reset-button"
        color="vtblue"
        large
        dark
        fixed
        bottom
        right
        @click="resetChartZoom"
      >
        {{ $t("common.labels.resetView") }}
        <v-icon right dark> mdi-restart </v-icon>
      </v-btn>
    </v-fade-transition>
    <div class="chart-container">
      <div id="lc" :style="`height: ${visible_charts.length * 180}px;`"></div>
    </div>
    <v-menu v-model="show_event_info" :position-x="x" :position-y="y" top offset-y>
      <v-alert
        v-if="selected_event"
        class="ma-0"
        border="bottom"
        :icon="events_config[selected_event.eventType].icon"
        :color="events_config[selected_event.eventType].textColor"
        colored-border
        dense
      >
        {{ event_text }}
      </v-alert>
    </v-menu>
  </div>
  <div v-else>No data to display</div>
</template>

<script>
import {
  lightningChart,
  emptyFill,
  ColorHEX,
  AxisTickStrategies,
  SolidLine,
  SolidFill,
  DashedLine,
  StipplePatterns,
  emptyLine,
  PointShape,
  emptyTick,
  ImageFill,
  UIElementBuilders,
  UIOrigins,
  ImageFitMode,
} from "@lightningchart/lcjs";
import { makeCustomTheme } from "@lightningchart/lcjs-themes";

import ChartOptions from "../components/ChartOptions";
import moment from "moment";
import { content } from "../../config/general.js";

export default {
  name: "LightningChartVT",
  components: {
    ChartOptions,
  },
  props: {
    chart_data: {
      type: Object,
      required: true,
    },
    new_chart_data: Object,
    config: Object,
    events_config: Object,
    charts: Array,
    types: Array,
    new_type_data: Object,
    real_time_data: Object,
    is_loading: Boolean,
    toggle_phase: Object,
    toggle_chart: Array,
    visible_charts: Array,
    visible_types: Array,
    settings_menu_props: Object,
    is_live_stream_on: {
      type: Boolean,
      default: true,
    },
    events_data: Array,
    under_development: {
      type: Boolean,
      default: false,
    },
  },
  data: function () {
    return {
      lc: null,
      lc_charts: [],
      LC_KEY: process.env["VUE_APP_LC_KEY"],
      general_config: null,
      line_series: {},
      is_chart_modified: false,
      data_point_size: 0,
      color_palette: {},
      color_palette_preference: "vt",
      visible_phases: { L1: true, L2: true, L3: true },
      final_values: null,
      show_event_info: false,
      x: 0,
      y: 0,
      selected_event: null,
    };
  },
  created() {
    this.general_config = content;
  },
  mounted() {
    this.initialize();
  },
  watch: {
    "$vuetify.theme.dark"() {
      // This to update the theme in the lightning chart
      // data cleaning should be first before re-rendering the chart
      this.$emit("resetChartOptions");
      this.lc_charts.forEach((chart) => {
        chart.dispose();
      });
      this.initialize();
      // TODO: Edge case: If the user toggled min and max values and then toggled the theme, the min and max values are lost
    },
    color_palette_preference: function (new_palette, old_palette) {
      if (new_palette && new_palette !== old_palette) {
        this.$emit("resetChartOptions");
        this.lc_charts.forEach((chart) => {
          chart.dispose();
        });
        this.initialize();
      }
    },
    toggle_phase() {
      // This to show or hide the phases;
      this.updateVisibleLines(this.toggle_phase);
      this.visible_phases[this.toggle_phase.value] = this.toggle_phase.show;
    },
    new_type_data: function (chart_data) {
      // This to adding new data to the existing charts(e.g min, max values on existing mean charts)
      if (chart_data.length === 0) return;
      this.charts.forEach((chart) => {
        const new_chart_data = chart_data[chart];
        new_chart_data.forEach((data, idx) => {
          const line_serie = this.line_series[chart][idx].chart
            .setTitle("")
            .addPointLineAreaSeries({
              axisY: this.line_series[chart][idx].axisY,
              dataPattern: "ProgressiveX",
              value: data.value,
              type: data.type,
              config_id: data.config_id,
            })
            .setMaxSampleCount(1000000)
            .setStrokeStyle((stroke) => stroke.setThickness(1))
            .setAreaFillStyle(emptyFill)
            .appendSamples({
              xValues: data.values.xValues,
              yValues: data.values.yValues,
            })
            .setStrokeStyle(
              new DashedLine({
                thickness: data.type === "Min" ? 0.5 : 1,
                fillStyle: new SolidFill({
                  color: this.getHexColor(
                    this.$vuetify.theme.themes[this.theme_name][
                      this.general_config.chart_palettes[this.color_palette_preference].line_colors[idx]
                    ]
                  ),
                }),
                pattern: data.type === "Min" ? StipplePatterns.Dashed : StipplePatterns.DottedDense,
                patternScale: data.type === "Min" ? 4 : 2,
              })
            )
            .setPointFillStyle(
              new SolidFill({
                color: this.getHexColor(
                  this.$vuetify.theme.themes[this.theme_name][
                    this.general_config.chart_palettes[this.color_palette_preference].line_colors[idx]
                  ]
                ),
              })
            )
            .setPointSize(this.data_point_size)
            .setName(data.label);
          this.line_series[chart].push(line_serie);
        });
      });
    },
    real_time_data: function (chart_data) {
      // This to add real-time data to the existing line series
      if (chart_data.length === 0) return;

      // If the live stream is off, we should not update the chart
      if (!this.is_live_stream_on) return;

      this.charts.forEach((chart) => {
        this.line_series[chart].forEach((serie, idx) => {
          serie
            .appendSamples({
              xValues: chart_data[chart][idx].values.xValues,
              yValues: chart_data[chart][idx].values.yValues,
            })
            // The reason for the below condition is to show the data only if the phase is visible and the type is visible by the user in the settings
            .setVisible(
              this.visible_types.includes(chart_data[chart][idx].type) &&
                this.visible_phases[chart_data[chart][idx].value]
            );
          // The mis-match for the "phase" and "value" strings is due to different naming conventions in the data
          // For most of the cases, the "value" is referring to the phase, however, in some cases, value might be different such as Ambient, Internal Temp, etc.
        });
      });
    },
    chart_data: function (new_data, old_data) {
      // This to update the chart data when the data(by the period) is changed
      if (new_data && new_data !== old_data) {
        this.$emit("resetChartOptions");
        this.lc_charts.forEach((chart) => {
          chart.dispose();
        });
        this.initialize();
      }
    },
    new_chart_data: function (charts) {
      // Charts is an object with the following format: { chart_name: { data: [] or null, show: true } }
      // Itereate over the this.line_series and update the visibility of the line series
      // If the data is not null, append the samples to the line series

      Object.keys(this.line_series).forEach((serie) => {
        this.line_series[serie].forEach((line, idx) => {
          if (!charts[serie]) {
            line.setVisible(false);
            line.axisY.setVisible(false);
            return;
          }
          if (charts[serie].data) {
            line.appendSamples({
              xValues: charts[serie].data[idx].values.xValues,
              yValues: charts[serie].data[idx].values.yValues,
            });
          }
          line.setVisible(charts[serie].show);
          line.axisY.setVisible(charts[serie].show);
        });
      });
    },
  },
  methods: {
    initialize() {
      this.lc = lightningChart({ license: this.LC_KEY });
      const container = document.getElementById("lc");
      this.lc.layout();

      const chart = this.lc.ChartXY({
        container: container,
        theme: this.theme,
        overrideInteractionMouseButtons: {
          chartXYPanMouseButton: 0, // LMB
          chartXYRectangleZoomFitMouseButton: 2, // RMB
        },
        defaultAxisX: {
          type: "linear-highPrecision",
        },
      });
      chart.axisY.dispose();

      // Setting the chart functionalities
      this.setTickMarker(chart);
      this.setChartCursor(chart);
      this.setCursorStyle(chart);
      this.setRectangleOptions(chart);
      this.formatResultTable(chart);

      this.charts.forEach((series, index) => {
        //? Adding the X and Y axis .setTitleRotation(0) is to make the title horizontal
        const axisY = chart
          .addAxisY({ iStack: Math.abs(index - this.charts.length) })
          .setAnimationScroll(true)
          .setMargins(index == this.charts.length - 1 ? 0 : 40, 0);
        const axisX = chart.getDefaultAxisX().setTitleFont((font) => font.setSize(12));

        // Adding the event icon to the chart only for the last chart
        if (index == this.charts.length - 1) this.addEventIcons(chart, axisX, axisY, this.events_data);

        this.line_series[series] = [];
        this.chart_data[series].forEach((serie, idx) => {
          // Setting the X axis properties
          this.setAxisXProps(axisX, serie.config_id);

          // Setting the Y axis properties
          this.setAxisYProps(axisY, serie, this.config[serie.config_id].suffix);

          // Setting the threshold lines
          this.setChartThresholds(axisY, this.config[serie.config_id]);

          // Setting the chart interactions
          //TODO: Migrate the chart interaction method for v7 LCJS
          this.onChartInteractions(chart, axisX, axisY);

          const line_serie = chart
            .setTitle("")
            .addPointLineAreaSeries({
              axisY,
              dataPattern: "ProgressiveX",
              value: serie.value,
              type: serie.type,
              feeder: serie.feeder,
              config_id: serie.config_id,
            })
            .setMaxSampleCount(1000000)
            .setStrokeStyle((stroke) => stroke.setThickness(1))
            .setAreaFillStyle(emptyFill)
            .appendSamples({
              xValues: serie.values.xValues,
              yValues: serie.values.yValues,
            })
            .setStrokeStyle(
              new SolidLine({
                thickness: 1,
                fillStyle: new SolidFill({
                  //color: this.getHexColor(this.config[serie.config_id][`${this.theme_name}_colors`][idx]),
                  color: this.getHexColor(
                    this.$vuetify.theme.themes[this.theme_name][
                      this.general_config.chart_palettes[this.color_palette_preference].line_colors[idx]
                    ]
                  ),
                }),
              })
            )
            .setPointFillStyle(
              new SolidFill({
                color: this.getHexColor(
                  this.$vuetify.theme.themes[this.theme_name][
                    this.general_config.chart_palettes[this.color_palette_preference].line_colors[idx]
                  ]
                ),
              })
            )
            .setPointSize(this.data_point_size)
            .setName(serie.feeder ? `${serie.feeder} ${serie.label}` : serie.label);
          this.line_series[series].push(line_serie);

          if (!this.config[serie.config_id].show || !this.visible_charts.includes(series)) {
            // If the chart is hidden initially, we need to hide the line series and the axisY
            line_serie.setVisible(false);
            axisY.setVisible(false);
          }
        });
      });
    },
    setRectangleOptions(chart) {
      // This function is to remove the rectangle zoom and fit options on the right mouse click
      chart.setMouseInteractionRectangleZoom(false);
      chart.setMouseInteractionRectangleFit(false);
    },
    setAxisXProps(axisX, config_id) {
      if (this.config[config_id].noDateTimeXaxis) {
        return axisX.setTickStrategy(AxisTickStrategies.Empty);
      }
      axisX.setTitle("Timestamp");
      return axisX.setTickStrategy(AxisTickStrategies.DateTime, (tickStrategy) =>
        tickStrategy
          .setMajorTickStyle((tickStyle) => tickStyle.setLabelFont((font) => font.setSize(12)))
          .setTickStyle((ticks) => ticks.setGridStrokeStyle(emptyLine))
          .setGreatTickStyle(emptyTick)
      );
    },
    setAxisYProps(axisY, serie, unit) {
      const title = this.getAxisYTitle(serie);
      axisY.setTitle(title).setTitleFont((font) => font.setSize(12));
      axisY.setChartInteractionZoomByWheel(false);
      axisY.setChartInteractionPanByDrag(false);
      axisY.setChartInteractionZoomByDrag(false);
      axisY.setMouseInteractions(true);
      axisY.setChartInteractions(false);
      axisY.setUnits(unit, {
        displayInCursor: true,
        displayOnAxis: true,
      });
    },
    setCursorStyle(chart) {
      // This function is to set the cursor style on the focused point
      const color =
        this.$vuetify.theme.themes[this.theme_name][
          this.general_config.chart_palettes[this.color_palette_preference].point_marker_color
        ];

      chart.setCursor((cursor) =>
        cursor
          .setPointMarker((pointMarker) =>
            pointMarker
              .setShape(PointShape.HollowCircle)
              .setSize({
                x: this.data_point_size < 5 ? 10 : this.data_point_size,
                y: this.data_point_size < 5 ? 10 : this.data_point_size,
              })
              .setFillStyle(new SolidFill({ color: this.getHexColor(color) }))
          )
          .setVisible(true)
      );
    },
    setTickMarker(chart) {
      // This function is to remove the tick markers on the y-axis
      chart.setCursor((cursor) => cursor.setTickMarkerXVisible(true).setTickMarkerYVisible(false));
    },
    setChartCursor(chart) {
      // This function is to remove the x-axis grid lines
      chart.setCursor((cursor) => cursor.setGridStrokeYStyle(emptyLine));
    },
    addThresholdLine(axisY, configValue, colorConfig, isSolid) {
      let lineStyle = isSolid
        ? new SolidLine({
            thickness: 0.1,
            fillStyle: new SolidFill({
              color: this.getHexColor(colorConfig),
            }),
          })
        : new DashedLine({
            thickness: 0.1,
            fillStyle: new SolidFill({
              color: this.getHexColor(colorConfig),
            }),
            pattern: StipplePatterns.Dashed,
            patternScale: 4,
          });

      return axisY.addConstantLine(true).setValue(configValue).setStrokeStyle(lineStyle).setMouseInteractions(false);
    },
    addBoundries(axis, avg) {
      let values = { lower: avg * 0.8, upper: avg * 1.2 };
      this.addThresholdLine(axis, values.lower, this.general_config.chart_threshold_colors.LowerAvg, true);
      this.addThresholdLine(axis, values.upper, this.general_config.chart_threshold_colors.UpperAvg, true);
    },
    setChartThresholds(axisY, config) {
      // This function is to set the threshold lines on the chart
      config.nom ? this.addThresholdLine(axisY, config.nom, this.general_config.chart_threshold_colors.Nom) : null;
      config.min ? this.addThresholdLine(axisY, config.min, this.general_config.chart_threshold_colors.Min) : null;
      config.max ? this.addThresholdLine(axisY, config.max, this.general_config.chart_threshold_colors.Max) : null;
      config.alert_limit && this.alert_limit
        ? this.addThresholdLine(axisY, this.alert_limit, this.general_config.chart_threshold_colors.Alert)
        : null;
      config.average_threshold && this.average_threshold && this.average_threshold[config.id].avg
        ? this.addBoundries(axisY, this.average_threshold[config.id].avg)
        : null;

      //TODO: If config.average_threshold is true, we need to addBoundries
    },
    onChartInteractions(chart, axisX, axisY) {
      // This function is to track the chart interactions to set the is_chart_modified flag
      chart.onSeriesBackgroundMouseDrag(() => {
        this.is_chart_modified = true;
      });

      chart.onSeriesBackgroundMouseWheel(() => {
        this.is_chart_modified = true;
      });

      axisX.onAxisInteractionAreaMouseWheel(() => {
        this.is_chart_modified = true;
      });

      axisX.onAxisInteractionAreaMouseDragStop(() => {
        this.is_chart_modified = true;
      });

      axisY.onAxisInteractionAreaMouseWheel(() => {
        this.is_chart_modified = true;
      });

      axisY.onAxisInteractionAreaMouseDragStop(() => {
        this.is_chart_modified = true;
      });
    },
    resetChartZoom() {
      // This function is to reset the chart zoom to the initial state
      this.charts.forEach((chart) => {
        this.line_series[chart].forEach((serie) => {
          serie.axisX.fit(true);
          serie.axisY.fit(true);
        });
      });
      this.is_chart_modified = false;
    },
    updateDataPoints(size) {
      // This function is to update the data point size
      // PS: No need to assign emptyFill when point size 0 as it is not improving the performance
      this.charts.forEach((chart) => {
        this.line_series[chart].forEach((serie) => {
          serie.setPointFillStyle(new SolidFill({ color: serie.getStrokeStyle().fillStyle.color })).setPointSize(size);
        });
      });
    },
    updateColorPalette(palette) {
      this.color_palette_preference = palette;
    },
    updateVisibleLines(payload) {
      // This function is to show or hide the asked phase or value(min, max, mean)
      if (payload.value) {
        this.charts.forEach((chart) => {
          this.line_series[chart].forEach((serie) => {
            if (
              serie.getName().includes(payload.value) &&
              this.visible_types.includes(serie.Xs.type) &&
              this.visible_charts.includes(chart)
            ) {
              serie.setVisible(payload.show);
            }
          });
        });
      }
      if (payload.type) {
        if (!this.types.includes(payload.type)) {
          // If we do not have the type data, we need to call the API and get the data
          // PS: API will be called only once for each type as the data will be stored in the chart_data object
          this.$emit("getTypeData", payload.type);
        }
        this.setVisibleTypes(payload.type, payload.show);

        this.charts.forEach((chart) => {
          this.line_series[chart].forEach((serie) => {
            if (serie.getName().includes(payload.type)) {
              serie.setVisible(payload.show);
            }
          });
        });
      }
    },
    getHexColor(color_hex) {
      // This method is to get the color from the color_palette object to avoid creating the same color multiple times
      // as the ColorHEX, ColorRGBA, etc. methods are heavy
      if (!this.color_palette[color_hex]) {
        this.color_palette[color_hex] = ColorHEX(color_hex);
      }
      return this.color_palette[color_hex];
    },
    setVisibleTypes(type, show) {
      // This function is to emit the visible types to the parent component to handle edge cases
      // e.g If do not keep track of the visible types, when we toggle the phases, the hidden types will be shown again
      this.$emit("setVisibleTypes", { type, show });
    },
    formatResultTable(chart) {
      // First part is to give stroke line around the result table
      chart.setCursor((cursor) =>
        cursor.setResultTable((resultTable) =>
          resultTable.setBackground((background) =>
            background.setStrokeStyle(
              new SolidLine({
                thickness: 0.3,
                fillStyle: new SolidFill({
                  color: this.$vuetify.theme.dark ? this.getHexColor("#ffffff") : this.getHexColor("#000000"),
                }),
              })
            )
          )
        )
      );
      // Second part is to format the result table
      chart.setCursorFormatting((_, hit, hits) => {
        // First, we need to get the values of the hit points with the following format: final_values[key] = { title, values: {}, suffix: h.axisY.getUnits(), colors: {} };
        let final_values = {};
        hits.forEach((h) => {
          let phase_color = h.series.getStrokeStyle().fillStyle.color;
          let current_config = this.config[h.series.Xs.config_id];
          let key;
          if (h.series.Xs.feeder) {
            //get the feeder name
            key = `${h.series.Xs.feeder}_${h.series.Xs.config_id}_${h.series.Xs.type}`;
          } else {
            key = `${h.series.Xs.config_id}_${h.series.Xs.type}`;
          }
          if (!final_values[key]) {
            const { feeder, type } = h.series.Xs;
            const formatted_type = type.charAt(0).toUpperCase() + type.slice(1);
            const title = `${feeder ? feeder + " " : ""}${current_config.label} ${formatted_type}`;
            final_values[key] = { title, values: {}, colors: {} };
          }

          current_config.values.forEach((value) => {
            if (h.series.Xs.value == value) {
              // Append to the values string
              final_values[key].values[value] = h.y;
              final_values[key].colors[value] = phase_color;
            }
          });
        });

        let formattedResults = [];

        // After getting the values, we need to format them to be displayed in the table
        for (const key in final_values) {
          if (Object.prototype.hasOwnProperty.call(final_values, key)) {
            const currentItem = final_values[key];

            const formatted = [
              { text: currentItem.title, font: { weight: "bold" } },
              [],
              ...Object.entries(currentItem.values).flatMap(([key, value]) => [
                `${key != "null" ? key : ""}:`,
                {
                  text: `${value.toFixed(2)}`,
                  fillStyle: new SolidFill({ color: currentItem.colors[key] }),
                },
              ]),
            ];

            formattedResults.push(formatted);
          }
        }

        return [[hit.axisX.formatValue(hit.x)], ...formattedResults];
      });
    },
    addEventIcons(chart, axisX, axisY, events) {
      // This function is to add an icon to the chart
      if (!events) return;

      events.forEach((event) => {
        const event_config =
          event.eventType in this.events_config ? this.events_config[event.eventType] : this.events_config["UNKNOWN"];
        const event_image = new Image();

        const svg_string = `
           <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
            <rect width="24" height="24" fill="${event_config.backgroundColorHEX}" />
            <path d="${event_config.svg_path}" fill="${event_config.textColorHEX}" />
          </svg>
        `;

        const svgBlob = new Blob([svg_string], { type: "image/svg+xml" });
        const svgUrl = URL.createObjectURL(svgBlob);

        event_image.src = svgUrl;

        event_image.onload = () => {
          const uiIcon = chart
            .addUIElement(UIElementBuilders.PointableTextBox, { x: axisX, y: axisY })
            .setPosition({
              x: moment(event.timestampISO).valueOf(),
              y: axisY.getInterval().start,
            })
            .setOrigin(UIOrigins.CenterBottom)
            .setTextFillStyle(emptyFill)
            .setPadding({ left: 16, top: 16 })
            .setDraggingMode(0) // Not draggable
            .setBackground((background) =>
              background.setStrokeStyle(emptyLine).setFillStyle(
                new ImageFill({
                  source: event_image,
                  fitMode: ImageFitMode.Center,
                  surroundingColor: this.getHexColor(event_config.backgroundColorHEX),
                })
              )
            );

          axisY.onIntervalChange(() => {
            uiIcon.setPosition({
              x: moment(event.timestampISO).valueOf(),
              y: axisY.getInterval().start,
            });
          });

          uiIcon.onMouseEnter((_, e) => {
            this.x = e.clientX;
            this.y = e.clientY;
            this.selected_event = event;
            this.show_event_info = !this.show_event_info;
          });
          uiIcon.onMouseLeave(() => {
            this.show_event_info = false;
          });
          uiIcon.onMouseClick(() => {
            // Send user to the event details page
            const url = `/event/${event.deviceId}/${event.event_id}/${event.subid}/${event.startTimestamp}`;

            //this.$router.push(url);
            // Open the URL in a new tab
            window.open(url, "_blank");
          });
        };
      });
    },
    getAxisYTitle(serie) {
      return serie.feeder
        ? `${serie.feeder} ${this.config[serie.config_id].yAxis}`
        : this.config[serie.config_id].yAxis;
    },
  },
  computed: {
    theme() {
      return makeCustomTheme({
        isDark: this.$vuetify.theme.dark,
        fontFamily: "Roboto, sans-serif",
        backgroundColor: this.$vuetify.theme.dark ? this.getHexColor("#1c1d21") : this.getHexColor("#ffffff"),
        textColor: this.$vuetify.theme.dark ? this.getHexColor("#ffffffaa") : this.getHexColor("1c1d2188"),
        dataColors: [this.getHexColor("#ffff5b"), this.getHexColor("#ffcd5b"), this.getHexColor("#ff9b5b")],
        axisColor: this.$vuetify.theme.dark ? this.getHexColor("#00000000") : this.getHexColor("1c1d2188"),
        gridLineColor: this.$vuetify.theme.dark ? this.getHexColor("#303030ff") : this.getHexColor("1c1d2122"),
        uiBackgroundColor: this.$vuetify.theme.dark ? this.getHexColor("#303030ff") : this.getHexColor("ffffff"),
        uiBorderColor: this.$vuetify.theme.dark ? this.getHexColor("#303030ff") : this.getHexColor("ffffff"),
        dashboardSplitterColor: this.$vuetify.theme.dark ? this.getHexColor("#552d2d66") : this.getHexColor("ffffff"),
        gradients: false,
      });
    },
    theme_name() {
      return this.$vuetify.theme.dark ? "dark" : "light";
    },
    event_text() {
      return `${this.events_config[this.selected_event.eventType].label} occured ${moment(this.selected_event.timestampISO).fromNow()}`;
    },
  },
  beforeDestroy() {
    // "dispose" should be called when the component is unmounted to free all the resources used by the chart
    this.lc.dispose();
    this.lc_charts.forEach((chart) => {
      chart.dispose();
    });
  },
};
</script>

<style scoped>
.reset-button {
  z-index: 100;
}

.chart-options {
  display: flex;
  flex-wrap: wrap;
}
</style>
